import { useUser } from '@clerk/react';
import { Button, TopAffix, LoadingIndicator } from '@vrplatform/components';
import {
  useBilling,
  useDashboard,
  useIntercom,
  useOwnerPortalContact,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { useAppName } from '@vrplatform/theme';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { Center, Group, Text, Title } from '@mantine/core';
import {
  DEMO_TEST_PARTNER_TENANT_ID,
  VRP_TENANT_ID,
} from '@vrplatform/ui-common';
import { type ReactNode, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router';
import { ViewReadySpan } from '../../performance/ViewReadySpan';
import { lazyWithRetry } from './_utils';
import {
  DashboardBillingWrapper,
  DashboardManagedTeamRegionsWrapper,
} from './_components';

const OwnerRoutes = lazyWithRetry(() =>
  import('./OwnerRoutes').then((module) => ({ default: module.OwnerRoutes }))
);
const PartnerRoutes = lazyWithRetry(() =>
  import('./PartnerRoutes').then((module) => ({
    default: module.PartnerRoutes,
  }))
);
const PmRoutes = lazyWithRetry(() =>
  import('./PmRoutes').then((module) => ({ default: module.PmRoutes }))
);
const VrpRoutes = lazyWithRetry(() =>
  import('./VrpRoutes').then((module) => ({ default: module.VrpRoutes }))
);

export const DashboardRoutes = () => (
  <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
    <DashboardRouteTree />
  </Suspense>
);

const DashboardRouteTree = () => {
  const [dashboard] = useDashboard();
  const [{ id: teamId }] = useTeam();

  if (dashboard === 'owner') {
    return (
      <SuspendedWrapper>
        <ViewReadySpan portal="owner" />
        <OwnerRoutes />
      </SuspendedWrapper>
    );
  }

  if (dashboard === 'overview') {
    return (
      <DashboardManagedTeamRegionsWrapper>
        <PartnerRoutes />
      </DashboardManagedTeamRegionsWrapper>
    );
  }

  if (dashboard === 'partner') {
    return (
      <Wrapper>
        {teamId === VRP_TENANT_ID ? <VrpRoutes /> : <PartnerRoutes />}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {dashboard === 'propertyManager' && <ViewReadySpan portal="pm" />}
      <PmRoutes />
    </Wrapper>
  );
};

const Wrapper = ({ children }: { children: ReactNode }) => {
  return (
    <StagingWrapper>
      <SuspendedWrapper>
        <DashboardManagedTeamRegionsWrapper>
          <DashboardBillingWrapper>
            <TwoFaWrapper>{children}</TwoFaWrapper>
          </DashboardBillingWrapper>
        </DashboardManagedTeamRegionsWrapper>
      </SuspendedWrapper>
    </StagingWrapper>
  );
};

const StagingWrapper = ({ children }: { children: ReactNode }) => {
  const { isVrpAdmin } = useTeamRole();
  const [overwriteQueryParam, setOverwriteQueryParam] = useQueryState(
    'vrp_staging_overwrite',
    parseAsString
  );

  useEffect(() => {
    const isDev =
      import.meta.env.DEV || window.location.hostname === 'localhost';
    const enableOverwrite = overwriteQueryParam === 'true';
    const stagingSessionStorage = sessionStorage.getItem(
      'vrp_staging_overwrite'
    );
    const stagingDomain = 'staging.portal.vrplatform.app';
    const prodDomain = 'portal.vrplatform.app';
    const isProdDomain = window.location.hostname === prodDomain;
    // do nothing in any of these cases
    if (!isVrpAdmin || isDev || stagingSessionStorage || !prodDomain) return;

    // redirect to staging
    if (isProdDomain && !enableOverwrite && !stagingSessionStorage) {
      window.location.href = `https://${stagingDomain}${window.location.pathname}${window.location.search}`;
      return;
    }

    // setSessionStorage if user was redirected and remove query param
    if (enableOverwrite) {
      sessionStorage.setItem('vrp_staging_overwrite', 'true');
      setOverwriteQueryParam(null, { history: 'replace' });
      return;
    }
  }, [isVrpAdmin, overwriteQueryParam, setOverwriteQueryParam]);

  return children;
};

const SuspendedWrapper = ({ children }: { children: ReactNode }) => {
  const [{ status, partnerId }] = useTeam();
  const { isVrpAdmin } = useTeamRole();
  const location = useLocation();
  const [dashboard] = useDashboard();
  const { show } = useIntercom();
  const { appName } = useAppName();
  const { showBilling } = useBilling();
  const { contactId } = useOwnerPortalContact();

  const isOwnerPortal = dashboard === 'owner';

  if (
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/features') ||
    showBilling ||
    status !== 'inactive'
  )
    return children;

  const isDirectTeam = [VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(
    partnerId
  );

  if (isOwnerPortal && !contactId) return children;

  if (isVrpAdmin) {
    return (
      <>
        <TopAffix themeColor="yellow">
          <Group gap={'xs'}>
            <Icon
              icon="AlertTriangleIcon"
              size={20}
              color={({ colors }) => colors.yellow[8]}
            />
            <Text size="xs" component="p" m={0}>
              Team is suspended
            </Text>
          </Group>
        </TopAffix>
        {children}
      </>
    );
  }

  return (
    <Center
      mih="90dvh"
      sx={(theme) => ({
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing.md,
        textAlign: 'center',
      })}
    >
      <Icon
        icon="Lock2Icon"
        color={({ colors }) => colors.yellow[6]}
        size={40}
      />
      {isOwnerPortal ? (
        <>
          <Title order={2} c="neutral">
            Property Manager Suspended
          </Title>
          <Text component="p" c="gray" my="sm" maw={550}>
            Your property managers access to {appName} has been temporarily
            suspended. Please get in touch with your property manager.
          </Text>
        </>
      ) : (
        <>
          <Title order={2} c="neutral">
            Team suspended
          </Title>
          <Text component="p" c="gray" my="sm" maw={550}>
            Your team was suspended.{' '}
            {isDirectTeam
              ? 'Please reach out to us using Help & Support.'
              : 'Please reach out to your partner to resolve the suspension.'}
          </Text>
          {isDirectTeam && (
            <Button
              variant="light"
              onClick={show}
              leftIcon={'CommentQuestionIcon'}
            >
              Help & Support
            </Button>
          )}
        </>
      )}
    </Center>
  );
};

const TwoFaWrapper = ({ children }: { children: ReactNode }) => {
  const [team] = useTeam();
  const { isVrpAdmin } = useTeamRole();
  const { user } = useUser();
  const location = useLocation();
  const { appName } = useAppName();

  const showChildren = team.settings.showTwoFactorAuth
    ? !!user?.twoFactorEnabled
    : true;

  if (
    isVrpAdmin ||
    showChildren ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/features')
  )
    return children;

  return (
    <Center
      mih="90%"
      sx={(theme) => ({
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing.md,
        textAlign: 'center',
      })}
    >
      <Icon
        icon="Lock2Icon"
        color={({ colors, primaryColor }) => colors[primaryColor][6]}
        size={40}
      />
      <Title order={2} c="neutral">
        Authentication required
      </Title>
      <Text component="p" c="gray" my="sm" maw={550}>
        Your team admins have required you to enable 2FA authentication. You can
        go into your settings and enable it to view data on {appName}.
      </Text>
      <Button
        variant="primary"
        component={Link}
        to="/settings/security"
        rightIcon={'ArrowRightIcon'}
      >
        Add 2FA authentication
      </Button>
    </Center>
  );
};
